/**
 * PTL "Still there?" prompt — a sticky toast/modal that appears when
 * the backend tells us the listener owes an active-confirmation tap.
 *
 * Reads `/api/ptl/status.active.needs_confirmation` and shows a blocking
 * prompt that won't dismiss until the listener clicks Yes (POSTs to
 * /api/ptl/confirm-active). Audio keeps playing — only earning is
 * paused until they confirm.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Headphones, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30_000;

export function StillListeningPrompt({ token }) {
  const [needs, setNeeds] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/ptl/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setNeeds(!!r.data?.active?.needs_confirmation);
      } catch {
        // user might not be enrolled — silent
      }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setConfirming(true);
    try {
      await axios.post(
        `${API}/ptl/confirm-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNeeds(false);
    } catch {
      // keep prompt visible — they can retry
    } finally {
      setConfirming(false);
    }
  };

  if (!needs) return null;

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10000] max-w-md w-[92%]"
      data-testid="ptl-still-listening-prompt"
      role="alert"
    >
      <div className="rounded-2xl border-2 border-[#FFDA33] bg-[#0a0a0a] shadow-[0_0_40px_rgba(255,218,51,0.4)] p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FFDA33]/15 flex items-center justify-center shrink-0">
            <Headphones size={18} className="text-[#FFDA33]" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-['Chakra_Petch'] uppercase text-base text-white font-bold tracking-wide">
              Still listening?
            </h4>
            <p className="text-sm text-[#aaa] font-mono mt-1 leading-relaxed">
              Tap to confirm — your plays will start banking again.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={confirm}
                disabled={confirming}
                className="flex-1 px-4 py-2.5 rounded-full bg-[#FFDA33] text-black font-mono uppercase tracking-widest text-xs font-bold hover:bg-[#FFE97A] active:scale-95 transition-all disabled:opacity-60"
                data-testid="ptl-confirm-active-btn"
              >
                {confirming ? 'Confirming…' : "I'm here"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StillListeningPrompt;
